import { useMemo } from 'react'
import rawConfig from '@/config/tap.config.json'
import type { ModuleName } from '@/constants/modules'
import { useAuthStore } from '@/store/auth.store'
import type { NavItem, NavSection, TapConfig } from '@/types/manifest'
import { isModuleActive } from '@/utils/module'

// The JSON is validated structurally by the TapConfig shape; cast once here so
// the rest of the app consumes a typed manifest.
const manifest = rawConfig as unknown as TapConfig

/**
 * Exposes the deployment manifest: active modules, branding, and the navigation
 * filtered by (a) active modules and (b) the user's permissions.
 *
 * Permission filtering fails CLOSED: an item that declares a permission is
 * hidden unless that permission is actually granted. An empty permission set
 * therefore hides every gated item rather than revealing all of them.
 *
 * This is safe to do now only because `/permissions/me` resolves for every
 * authenticated role (see useSessionEnrichment). While it is still in flight the
 * rail shows ungated items only; after first login the persisted user carries
 * permissions, so there is nothing to flash on reload.
 *
 * The server enforces permissions on every request regardless — this filtering
 * is the UI half of the same rule, not a substitute for it.
 */
export function useManifest() {
  const permissions = useAuthStore((s) => s.user?.permissions)

  const navSections = useMemo<NavSection[]>(() => {
    const granted = new Set(permissions ?? [])
    const itemAllowed = (item: NavItem): boolean => {
      if (!isModuleActive(manifest.modules, item.module)) return false
      if (!item.permission) return true
      const resource = item.permission.split(':')[0]
      return granted.has(item.permission) || (resource ? granted.has(`${resource}:manage`) : false)
    }

    return manifest.nav
      .map((section) => ({ ...section, items: section.items.filter(itemAllowed) }))
      .filter((section) => section.items.length > 0)
  }, [permissions])

  return {
    modules: manifest.modules,
    brand: manifest.brand,
    navSections,
    isModuleActive: (name: ModuleName) => isModuleActive(manifest.modules, name),
  }
}
